import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import StripeConnectManager from './StripeConnectManager';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { BASE_URL } from '@/config/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, ShoppingBag, Ticket, Plus, Edit, Trash, CreditCard, CheckCircle2, AlertCircle } from 'lucide-react';


type ShopProductType = 'tickets' | 'pass';

interface ShopProduct {
  id: string;
  title: string;
  description: string;
  price: number;
  quantity: number;
  type?: ShopProductType | string | null;
  category_id?: string | number | null;
  amount?: number | null;
  amount_min?: number | null;
  amount_max?: number | null;
  created_at: string;
  updated_at: string;
}

interface ProductPayload {
  title: string;
  description: string;
  price: number;
  quantity: number;
  type: ShopProductType;
  category_id: string | null;
  amount: number | null;
  amount_min: number | null;
  amount_max: number | null;
}

interface FixedCategory {
  id: string;
  name: string;
  s_std_pay_type?: string;
  prices?: Record<string, number>;
}

interface PaymentMethod {
  type: string;
  details?: {
    status: number;
  };
}

interface User {
  paymentMethods: PaymentMethod[];
}

interface ShopManagerProps {}

const ShopManager: React.FC<ShopManagerProps> = () => {
  const { toast } = useToast();
  const { token, user, refreshUserData } = useAuth();
  const { t, locale } = useLanguage();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [quantity, setQuantity] = useState('5');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentProductId, setCurrentProductId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<ShopProduct | null>(null);
  const [isShopEnabled, setIsShopEnabled] = useState<boolean>(false);
  const [showStripeConfig, setShowStripeConfig] = useState<boolean>(false);
  const [productType, setProductType] = useState<ShopProductType>('tickets');
  const [categoryId, setCategoryId] = useState<string>('none');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const navigate = useNavigate();

  const ticketQuantities = ['1', '2', '3', '4', '5', '10', '20', '50', '100'];

  const { data: fixedCategories = [] } = useQuery({
    queryKey: ['shop-fixed-categories', token],
    queryFn: async () => {
      const response = await fetch(`${BASE_URL}/api/categories`, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch categories');
      }
      const data = await response.json();
      const cats: FixedCategory[] = data.categories || [];
      return cats.filter(
        (c) => String(c.s_std_pay_type) === '1' && Number(c.prices?.price_fix ?? 0) > 0
      );
    },
    enabled: !!token,
  });

  const handleCategoryChange = (value: string) => {
    setCategoryId(value);
    const cat = fixedCategories.find((c) => String(c.id) === value);
    if (cat?.prices?.price_fix) {
      setPrice(String(cat.prices.price_fix));
    }
  };

  useEffect(() => {
    if (user && user.paymentMethods) {
      const hasStripeEnabled = user.paymentMethods.some(
        method => method.type === 'Stripe' && method.details?.status === 1
      );
      setIsShopEnabled(hasStripeEnabled);
    } else {
      setIsShopEnabled(false);
    }
  }, [user]);

  const handleStripeConfigComplete = async () => {
    await refreshUserData();
    setShowStripeConfig(false);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) {
      return 'Date Invalide';
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return 'Date Invalide';
    }
    return new Intl.DateTimeFormat(locale === 'fr' ? 'fr-FR' : 'en-US').format(date);
  };

  const formatPrice = (priceValue: number) => {
    return new Intl.NumberFormat(locale === 'fr' ? 'fr-FR' : 'en-US', {
      style: 'currency',
      currency: 'EUR',
    }).format(priceValue);
  };

  const { data: products = [], isLoading } = useQuery({
    queryKey: ['shop-products', token],
    queryFn: async () => {
      const response = await fetch(`${BASE_URL}/api/shop/products`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch shop products');
      }
      const data = await response.json();
      return data || [];
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: ProductPayload) => {
      const response = await fetch(`${BASE_URL}/api/shop/create/product`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const validationErrors = errorData?.errors
          ? Object.values(errorData.errors).flat().join(' ')
          : '';
        throw new Error(validationErrors || errorData?.message || 'Failed to create product');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shop-products'] });
      resetForm();
      setIsModalOpen(false);
      toast({
        title: t('shop.productCreated'),
        description: t('shop.productCreatedDescription'),
      });
    },
    onError: (error) => {
      toast({
        title: t('common.error'),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: ProductPayload & { id: string }) => {
      const response = await fetch(`${BASE_URL}/api/shop/update/product`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const validationErrors = errorData?.errors
          ? Object.values(errorData.errors).flat().join(' ')
          : '';
        throw new Error(validationErrors || errorData?.message || 'Failed to update product');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shop-products'] });
      resetForm();
      setIsModalOpen(false);
      toast({
        title: t('shop.productUpdated'),
        description: t('shop.productUpdatedDescription'),
      });
    },
    onError: (error) => {
      toast({
        title: t('common.error'),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`${BASE_URL}/api/shop/delete/product`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete product');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shop-products'] });
      toast({
        title: t('shop.productDeleted'),
        description: t('shop.productDeletedDescription'),
      });
    },
    onError: (error) => {
      toast({
        title: t('common.error'),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setPrice('');
    setQuantity('5');
    setProductType('tickets');
    setCategoryId('none');
    setAmountMin('');
    setAmountMax('');
    setIsEditing(false);
    setCurrentProductId(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const isPass = productType === 'pass';
    if (!title || (isPass ? (!amountMin || !amountMax) : !price) || (!isPass && !quantity)) {
      toast({
        title: t('common.error'),
        description: t('shop.fillAllFields'),
        variant: "destructive",
      });
      return;
    }
    const parsedMin = isPass ? parseFloat(amountMin) : NaN;
    const parsedMax = isPass ? parseFloat(amountMax) : NaN;
    if (isPass && (isNaN(parsedMin) || parsedMin <= 0 || isNaN(parsedMax) || parsedMax <= parsedMin)) {
      toast({
        title: t('common.error'),
        description: t('shop.invalidRange'),
        variant: "destructive",
      });
      return;
    }
    const parsedValue = isPass ? parsedMin : parseFloat(price);
    if (!isPass && (isNaN(parsedValue) || parsedValue <= 0)) {
      toast({
        title: t('common.error'),
        description: t('shop.invalidPrice'),
        variant: "destructive",
      });
      return;
    }
    
    const payload: ProductPayload = {
      title,
      description: description?.trim() ? description.trim() : null,
      price: parsedValue,
      quantity: isPass ? 1 : parseInt(quantity),
      type: productType,
      category_id: !isPass && categoryId !== 'none' ? categoryId : null,
      amount: null,
      amount_min: isPass ? parsedMin : null,
      amount_max: isPass ? parsedMax : null,
    };
    setIsSubmitting(true);
    try {
      if (isEditing && currentProductId) {
        await updateMutation.mutateAsync({ id: currentProductId, ...payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditProduct = (product: ShopProduct) => {
    const type: ShopProductType = product.type === 'pass' ? 'pass' : 'tickets';
    setTitle(product.title);
    setDescription(product.description);
    setPrice(product.price.toString());
    setQuantity(product.quantity ? product.quantity.toString() : '5');
    setProductType(type);
    setCategoryId(product.category_id ? String(product.category_id) : 'none');
    setAmountMin(type === 'pass' ? String(product.amount_min ?? product.amount ?? '') : '');
    setAmountMax(type === 'pass' ? String(product.amount_max ?? '') : '');
    setIsEditing(true);
    setCurrentProductId(product.id);
    setIsModalOpen(true);
  };

  const handleAddProduct = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const handleDeleteProduct = (product: ShopProduct) => {
    setProductToDelete(product);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (productToDelete) {
      await deleteMutation.mutateAsync(productToDelete.id);
      setDeleteDialogOpen(false);
      setProductToDelete(null);
    }
  };

  if (!isShopEnabled) {
    return (
      <div className="space-y-6">
        <Card className="border-dashed">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <CreditCard className="h-5 w-5" />
              {t('invoices.settings.paymentInfo')}
            </CardTitle>
            <CardDescription>
              {t('invoices.settings.paymentMethods.stripeDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3 rounded-lg border bg-amber-50 border-amber-200 p-4">
              <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">{t('shop.shopNotEnable')}</p>
            </div>
            <StripeConnectManager mode="onboarding" onComplete={handleStripeConfigComplete} />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Tabs defaultValue="products" className="w-full">
        <TabsList className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="stripe" disabled className="opacity-100 data-[disabled]:opacity-60">
            <CheckCircle2 className="h-4 w-4 mr-2 text-green-600" />
            {t('shop.manageStripeAccount')}
          </TabsTrigger>
          <TabsTrigger value="payments">
            <CreditCard className="h-4 w-4 mr-2" />
            {t('shop.showPayments')}
          </TabsTrigger>
          <TabsTrigger value="products">
            <ShoppingBag className="h-4 w-4 mr-2" />
            {t('shop.yourProducts')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="payments" className="mt-4">
          <Card className="border-none bg-transparent shadow-none">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <CreditCard className="h-5 w-5" />
                {t('shop.showPayments')}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3">
              <StripeConnectManager mode="payments" />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="products" className="mt-4">
          <Card className="border-none bg-transparent shadow-none">
            <CardHeader className="flex flex-row items-start justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <ShoppingBag className="h-5 w-5" />
                  {t('shop.yourProducts')}
                </CardTitle>
                <CardDescription>{t('shop.yourProductsDescription')}</CardDescription>
              </div>
              <Button onClick={handleAddProduct} className="shrink-0">
                <Plus className="h-4 w-4 mr-2" />
                {t('shop.addProduct')}
              </Button>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex items-center justify-center p-8">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              ) : products.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                  <Ticket className="h-12 w-12 mb-2" />
                  <p>{t('shop.noProducts')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {products.map((product: ShopProduct) => {
                    const isPass = product.type === 'pass';
                    const passMin = Number(product.amount_min ?? product.amount ?? product.price ?? 0);
                    const passMax = Number(product.amount_max ?? product.amount ?? product.price ?? 0);
                    const linkedCategory = fixedCategories.find(
                      (c) => String(c.id) === String(product.category_id ?? '')
                    );
                    return (
                    <div key={product.id} className="flex items-center justify-between p-4 border border-dashed rounded-lg hover:bg-gray-50">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-medium truncate">{product.title}</h3>
                          <Badge variant="outline">
                            {isPass ? t('shop.typePass') : `${product.quantity} ${t('shop.tickets')}`}
                          </Badge>
                          {linkedCategory && (
                            <Badge variant="outline">{linkedCategory.name}</Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">{product.description}</p>
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          {isPass ? (
                            <Badge variant="secondary">
                              {formatPrice(passMin)} – {formatPrice(passMax)}
                            </Badge>
                          ) : (
                            <>
                              <Badge variant="secondary">
                                {formatPrice(product.price)} / {t('shop.ticket')}
                              </Badge>
                              <span className="text-sm text-muted-foreground">
                                {t('shop.totalPrice')}: {formatPrice(product.price * product.quantity)}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" onClick={() => handleEditProduct(product)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteProduct(product)}>
                          <Trash className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>


      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('shop.confirmDelete')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('shop.confirmDeleteDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <AlertDialogContent className="max-w-xl w-full max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>
                {isEditing ? t('shop.editProduct') : t('shop.createProduct')}
            </AlertDialogTitle>
            <AlertDialogDescription>
                {isEditing ? t('shop.editProductDescription') : t('shop.createProductDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-5">
            {/*<div className="space-y-2">
              <Label>{t('shop.productType')}</Label>
              <div className="grid grid-cols-2 gap-3">
                {(['tickets', 'pass'] as ShopProductType[]).map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setProductType(type)}
                    className={`rounded-lg border p-3 text-left transition-colors ${
                      productType === type
                        ? 'border-primary bg-primary/5 ring-1 ring-primary'
                        : 'border-dashed hover:bg-muted/50'
                    }`}
                  >
                    <div className="flex items-center gap-2 font-medium">
                      {type === 'tickets' ? <Ticket className="h-4 w-4" /> : <CreditCard className="h-4 w-4" />}
                      {type === 'tickets' ? t('shop.typeTickets') : t('shop.typePass')}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {type === 'tickets' ? t('shop.typeTicketsHelp') : t('shop.typePassHelp')}
                    </p>
                  </button>
                ))}
              </div>
            </div>*/}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title">{t('shop.name')}</Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={t('shop.titlePlaceholder')}
                />
              </div>
              {productType === 'tickets' && (
                <div className="space-y-2">
                  <Label htmlFor="quantity">{t('shop.quantity')}</Label>
                  <Select value={quantity} onValueChange={setQuantity}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('shop.selectQuantity')} />
                    </SelectTrigger>
                    <SelectContent>
                      {ticketQuantities.map((qty) => (
                        <SelectItem key={qty} value={qty}>
                          {qty} {t('shop.tickets')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">{t('shop.description')}</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('shop.descriptionPlaceholder')}
                rows={3}
              />
            </div>
            {productType === 'tickets' ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="category">{t('shop.fixedCategory')}</Label>
                  <Select value={categoryId} onValueChange={handleCategoryChange}>
                    <SelectTrigger id="category">
                      <SelectValue placeholder={t('shop.selectFixedCategory')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t('shop.noCategory')}</SelectItem>
                      {fixedCategories.map((cat) => (
                        <SelectItem key={cat.id} value={String(cat.id)}>
                          {cat.name} — {formatPrice(Number(cat.prices?.price_fix ?? 0))}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-foreground">{t('shop.fixedCategoryHelp')}</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="price">{t('shop.pricePerTicket')}</Label>
                  <Input
                    id="price"
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    placeholder="0.00"
                  />
                  <p className="text-sm text-muted-foreground">
                    {t('shop.priceHelp')}
                  </p>
                </div>
              </>
            ) : (
              <div className="space-y-2">
                <Label>{t('shop.passAmount')}</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="amount_min" className="text-xs text-muted-foreground">{t('shop.passMinAmount')}</Label>
                    <Input
                      id="amount_min"
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={amountMin}
                      onChange={(e) => setAmountMin(e.target.value)}
                      placeholder="100.00"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="amount_max" className="text-xs text-muted-foreground">{t('shop.passMaxAmount')}</Label>
                    <Input
                      id="amount_max"
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={amountMax}
                      onChange={(e) => setAmountMax(e.target.value)}
                      placeholder="500.00"
                    />
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">{t('shop.passRangeHelp')}</p>
              </div>
            )}
            <AlertDialogFooter className="mt-6">
              <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : isEditing ? (
                  t('common.update')
                ) : (
                  t('common.done')
                )}
              </Button>
            </AlertDialogFooter>
          </form>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ShopManager;